/**
 * The two calls to the public Memorial Wall (server/wallApi.mjs), in the shape of
 * `cloudSave/transport.ts`: nothing here throws and nothing here logs. A server that is missing,
 * asleep or answering the game's HTML is an ordinary outcome - the wall is an extra, never a gate,
 * and the local wall is always there whatever this returns.
 */

import type { MemorialEntry } from '../../meta/types';
import type { FetchLike } from '../cloudSave/types';

const REQUEST_TIMEOUT_MS = 12000;
/**
 * The server's own ceiling (server/wallApi.mjs), so an oversize entry is never sent to be refused. PHOTOS §2: 16 KB
 * for the entry plus the base64 of an 80 KB picture (`MAX_PHOTO_BYTES`), 128 KB in all.
 */
export const MAX_WALL_BODY_BYTES = 128 * 1024;

/** One name on the public wall: the entry, plus what the server stamped on it. */
export interface PublicName {
  /** The submitter's save id - a pseudonym the server derives without ever learning the code. */
  by: string;
  id: string;
  /** Server time the name went up, ms since the epoch. Never the client's clock. */
  at: number;
  entry: MemorialEntry;
  /** PHOTOS: the server holds a picture for this name, served at `/api/memorial/photo/<by>/<id>`. */
  photo?: boolean;
}

export type WallResult =
  | { kind: 'ok'; names: PublicName[]; count: number; mine: string | null }
  | { kind: 'unreachable' };

export type PublishResult =
  | { kind: 'ok'; name: PublicName; mine: number; cap: number }
  /** The server understood and said no - no cloud save yet, or too many names. Trying again will not help. */
  | { kind: 'refused'; status: number; why: string }
  | { kind: 'unreachable' };

function timeout(ms: number): AbortSignal | undefined {
  const able = typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function';
  return able ? AbortSignal.timeout(ms) : undefined;
}

const transient = (status: number): boolean => status >= 500 || status === 429 || status === 408;

/** Rebuilt field by field, exactly as the server does: a wall entry is only what the screen can draw. */
function asName(raw: unknown): PublicName | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const { by, id, at, entry } = r;
  if (typeof by !== 'string' || typeof id !== 'string' || typeof at !== 'number') return null;
  if (typeof entry !== 'object' || entry === null) return null;
  const e = entry as Record<string, unknown>;
  if (typeof e['ranger'] !== 'object' || e['ranger'] === null) return null;
  return { by, id, at, entry: entry as unknown as MemorialEntry, ...(r['photo'] === true ? { photo: true } : {}) };
}

/** Base64 of the bytes, in slices: one spread of 80 KB of arguments would overflow the stack. */
export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

/** How many campaigns have seen action lately: saves WRITTEN in each window, never their contents. */
export interface Pulse {
  day: number;
  week: number;
}

export interface WallTransport {
  /** Null when the server says nothing: the title simply shows no line. */
  pulse(): Promise<Pulse | null>;
  /** The wall, newest first. `mine` is this device's save id when a code was given. */
  read(code: string | null): Promise<WallResult>;
  /** PHOTOS: `photo`, when given, goes up in the same PUT as base64 under `jpeg` (PHOTOS-contracts §2). */
  publish(code: string, entry: MemorialEntry, photo?: Uint8Array | null): Promise<PublishResult>;
}

export function createWallTransport(fetchLike: FetchLike, origin: string): WallTransport {
  const url = `${origin}/api/memorial`;

  return {
    async pulse() {
      try {
        const signal = timeout(REQUEST_TIMEOUT_MS);
        const res = await fetchLike(`${origin}/api/pulse`, { cache: 'no-store', ...(signal ? { signal } : {}) });
        if (!res.ok) return null;
        const body = (await res.json()) as Record<string, unknown> | null;
        const day = body?.['day'];
        const week = body?.['week'];
        if (typeof day !== 'number' || typeof week !== 'number') return null;
        return { day: Math.max(0, Math.round(day)), week: Math.max(0, Math.round(week)) };
      } catch {
        return null;
      }
    },

    async read(code) {
      try {
        const signal = timeout(REQUEST_TIMEOUT_MS);
        const res = await fetchLike(url, {
          cache: 'no-store',
          ...(code === null ? {} : { headers: { Authorization: `Bearer ${code}` } }),
          ...(signal ? { signal } : {}),
        });
        if (!res.ok) return { kind: 'unreachable' };
        const body = (await res.json()) as Record<string, unknown> | null;
        const rows = body?.['entries'];
        if (!Array.isArray(rows)) return { kind: 'unreachable' };
        const names = rows.map(asName).filter((n): n is PublicName => n !== null);
        const count = body?.['count'];
        const mine = body?.['mine'];
        return {
          kind: 'ok', names,
          count: typeof count === 'number' ? count : names.length,
          mine: typeof mine === 'string' ? mine : null,
        };
      } catch {
        return { kind: 'unreachable' };
      }
    },

    async publish(code, entry, photo) {
      try {
        // The picture only when he chose to show it: absent or null, the name goes up alone.
        const body = JSON.stringify(photo && photo.length > 0 ? { ...entry, jpeg: toBase64(photo) } : entry);
        if (new TextEncoder().encode(body).length > MAX_WALL_BODY_BYTES) {
          return { kind: 'refused', status: 413, why: 'THAT RECORD IS TOO LONG TO PUBLISH.' };
        }
        const signal = timeout(REQUEST_TIMEOUT_MS);
        const res = await fetchLike(url, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${code}`, 'Content-Type': 'application/json' },
          body,
          cache: 'no-store',
          ...(signal ? { signal } : {}),
        });
        if (!res.ok) {
          if (transient(res.status)) return { kind: 'unreachable' };
          const said = (await res.json().catch(() => null)) as Record<string, unknown> | null;
          const why = typeof said?.['error'] === 'string' ? said['error'].toUpperCase() : 'THE WALL WOULD NOT TAKE IT.';
          return { kind: 'refused', status: res.status, why };
        }
        const raw = (await res.json()) as Record<string, unknown> | null;
        const name = asName(raw?.['entry']);
        if (name === null) return { kind: 'unreachable' };
        const mine = raw?.['mine'];
        const cap = raw?.['cap'];
        return {
          kind: 'ok', name,
          mine: typeof mine === 'number' ? mine : 0,
          cap: typeof cap === 'number' ? cap : 0,
        };
      } catch {
        return { kind: 'unreachable' };
      }
    },
  };
}
